import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "../lib/api";

const imgSrc = (p) => {
  if (!p) return "";
  return p.startsWith("http") || p.startsWith("data:") ? p : `${API}/files/${p}`;
};

/**
 * Discreet homepage carousel — shows multiple portrait tiles at once (5 on desktop /
 * 3 on tablet / 2 on mobile), rounded corners, small circular side arrows.
 * Auto-advances one tile every 5s (pauses on hover). Ideal for showcasing offers.
 */
export default function HomepageCarousel({ slides = [], accent = "#FF3B30" }) {
  const [startIdx, setStartIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  const valid = (slides || []).filter((s) => (s?.image_url || "").trim().length > 0);
  const total = valid.length;

  // Responsive: how many tiles fit at a time
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setVisibleCount(2);
      else if (w < 1024) setVisibleCount(3);
      else if (w < 1280) setVisibleCount(4);
      else setVisibleCount(5);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  // Auto-advance one tile every 5s
  useEffect(() => {
    if (paused || total <= visibleCount) return;
    timerRef.current = setInterval(() => {
      setStartIdx((i) => (i + 1) % Math.max(1, total));
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [paused, total, visibleCount]);

  if (total === 0) return null;

  // We build a "looped" order: rotate the array so startIdx becomes first,
  // then only render the first (visibleCount) tiles.
  const rotated = [...valid.slice(startIdx), ...valid.slice(0, startIdx)];
  const shown = rotated.slice(0, Math.min(visibleCount, total));

  const canPaginate = total > visibleCount;
  const prev = () => setStartIdx((i) => (i - 1 + total) % total);
  const next = () => setStartIdx((i) => (i + 1) % total);

  return (
    <section
      className="relative max-w-[1400px] mx-auto px-6 lg:px-14 py-10"
      data-testid="homepage-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative" ref={containerRef}>
        {/* Left arrow — outside the tiles, subtle */}
        {canPaginate && (
          <button
            type="button"
            onClick={prev}
            className="hidden md:flex absolute -left-3 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-neutral-200/80 hover:bg-neutral-300 items-center justify-center transition-colors shadow-sm"
            aria-label="Anterior"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-5 h-5 text-neutral-700" />
          </button>
        )}

        {/* Tiles row */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {shown.map((slide, i) => {
            const inner = (
              <>
                <img
                  src={imgSrc(slide.image_url)}
                  alt={slide.title || "Slide"}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                {(slide.subtitle || slide.title) && (
                  <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-white">
                    {slide.subtitle && (
                      <div className="text-[10px] md:text-[11px] font-mono uppercase tracking-wider mb-1 opacity-90" style={{ color: accent }}>
                        {slide.subtitle}
                      </div>
                    )}
                    {slide.title && (
                      <div className="font-display text-base md:text-lg lg:text-xl tracking-tight leading-tight line-clamp-2">
                        {slide.title}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
            const commonCls =
              "group relative aspect-[3/4] overflow-hidden rounded-2xl border border-neutral-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all";
            return slide.link ? (
              <Link
                key={`${startIdx}-${i}`}
                to={slide.link}
                className={commonCls}
                data-testid={`carousel-slide-${i}`}
              >
                {inner}
              </Link>
            ) : (
              <div key={`${startIdx}-${i}`} className={commonCls} data-testid={`carousel-slide-${i}`}>
                {inner}
              </div>
            );
          })}
        </div>

        {/* Right arrow */}
        {canPaginate && (
          <button
            type="button"
            onClick={next}
            className="hidden md:flex absolute -right-3 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-neutral-200/80 hover:bg-neutral-300 items-center justify-center transition-colors shadow-sm"
            aria-label="Siguiente"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-5 h-5 text-neutral-700" />
          </button>
        )}
      </div>

      {/* Mobile controls + dots (only when more slides than visible) */}
      {canPaginate && (
        <div className="mt-5 flex items-center justify-center gap-4 md:hidden">
          <button
            type="button"
            onClick={prev}
            className="w-9 h-9 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
            aria-label="Anterior"
            data-testid="carousel-prev-mobile"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-1.5" data-testid="carousel-dots">
            {valid.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStartIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === startIdx ? "w-6 bg-neutral-800" : "w-1.5 bg-neutral-300 hover:bg-neutral-500"}`}
                aria-label={`Ir a slide ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={next}
            className="w-9 h-9 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
            aria-label="Siguiente"
            data-testid="carousel-next-mobile"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}
